import {
  MimecastAwsRegions,
  MimecastEnvs,
  createAwsRumClient,
} from "./createAwsRumClient"

export function getMimecastCiEnv(): {
  region: MimecastAwsRegions
  env: MimecastEnvs
} {
  const { hostname = "" } = window.location

  if (hostname === "localhost") {
    return { region: "eu-west-2", env: "LOCAL" }
  }

  if (hostname.endsWith("dev.mimecastcsp.com")) {
    return { region: "eu-west-2", env: "DEV" }
  }

  if (hostname.endsWith("mimecastcsp-stg.com")) {
    return { region: "eu-west-2", env: "STG" }
  }

  const prodEnvs: { [key: string]: ReturnType<typeof getMimecastCiEnv> } = {
    "au-1.admin.mimecast.com": { region: "ap-southeast-2", env: "PROD" },
    "ca-1.admin.mimecast.com": { region: "ca-central-1", env: "PROD" },
    "de-1.admin.mimecast.com": { region: "eu-central-1", env: "PROD" },
    "uk-1.admin.mimecast.com": { region: "eu-west-2", env: "PROD" },
    "us-1.admin.mimecast.com": { region: "us-east-1", env: "PROD" },
    "us-4.admin.mimecast.com": { region: "us-west-2", env: "PROD" },
    "za-1.admin.mimecast.com": { region: "af-south-1", env: "PROD" },
  }

  return prodEnvs[hostname] ?? { region: "UNKNOWN", env: "UNKNOWN" }
}

export function createMimecastCiAwsRum({ ciApiUrl }: { ciApiUrl: string }) {
  try {
    const { env, region } = getMimecastCiEnv()

    const [subdomain] = window.location.hostname.split(".")

    const { appId, identityPoolId } = getRumConfig(env, region)

    return createAwsRumClient({
      appId,
      identityPoolId,
      mimecastAwsRegion: region,
      mimecastEnv: env,
      attrs: {
        SUBDOMAIN: subdomain,
      },
      baseUrl: ciApiUrl,
    })
  } catch (e) {
    // Ignore errors during initialization so RUM doesn't block the app from actually working
    console.error("Failed to init RUM:", e)
    return null
  }
}

function getRumConfig(
  env: MimecastEnvs,
  region: MimecastAwsRegions,
): { appId: string; identityPoolId: string } {
  switch (env) {
    case "PROD":
      return {
        "ap-southeast-2": {
          appId: "28725b5d-8b21-4654-b06f-5ec8c2474f04",
          identityPoolId: "ap-southeast-2:c1d63d6e-a841-49ba-8488-68a739a72af1",
        },
        "ca-central-1": {
          appId: "1657bca4-2dc2-46a9-ad5f-b4aa99232ea7",
          identityPoolId: "ca-central-1:587e5ec8-de34-42d6-a116-ea41ebe14d43",
        },
        "eu-central-1": {
          appId: "d63e48cd-1a22-46c9-878d-b557a2d293ec",
          identityPoolId: "eu-central-1:8e896d50-cf03-41ec-82f0-cc500e7497c8",
        },
        "eu-west-2": {
          appId: "1634f646-c9e3-47a0-8a23-02d88a657f35",
          identityPoolId: "eu-west-2:4df5e147-24ae-4cbd-9366-19e2f4527f80",
        },
        "us-east-1": {
          appId: "7cf39009-17a0-4217-b26a-318600e98801",
          identityPoolId: "us-east-1:3af6855d-624b-4e03-b768-bde78e532f84",
        },
        "us-west-2": {
          appId: "eb7e283f-47b5-4656-baea-1d2de02c928f",
          identityPoolId: "us-west-2:2379f6ea-969f-4caa-9e63-daef9c0fcae1",
        },
        "af-south-1": {
          appId: "71d8a7eb-6ccc-4faf-a305-2a97522bc986",
          identityPoolId: "af-south-1:cd1dc872-c462-472e-ab1a-42b9efc7650b",
        },
        UNKNOWN: { appId: "", identityPoolId: "" },
      }[region]
    case "STG":
      return {
        appId: "3c18b9e4-4055-4657-b20d-3fd498026ae7",
        identityPoolId: "eu-west-2:2695dbc4-3305-4f01-95cb-d0a3af051e31",
      }
    case "DEV":
      return {
        appId: "28382272-dde9-4555-b090-8ea361f61efd",
        identityPoolId: "eu-west-2:ec5bea5c-bccf-4140-9637-0953d503b4c6",
      }
    case "LOCAL":
      return {
        appId: "0d3dcaa4-0d49-4363-8eb6-49f4871f2be0",
        identityPoolId: "eu-west-2:7e66f95a-7698-4a94-92fe-1a28b1f8df3e",
      }
    case "UNKNOWN":
    default:
      return { appId: "", identityPoolId: "" }
  }
}
